export const REDACTED = ':redacted';

const ID_SEGMENT_PATTERN =
  /\/([a-zA-Z][\w-]*)\/(\d+|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})(?=\/|$)/gi;

const JWT_PATTERN = /eyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]*/g;
const EMAIL_PATTERN = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g;
const BEARER_PATTERN = /Bearer\s+[A-Za-z0-9._-]+/gi;
const SAML_XML_PATTERN = /<(?:samlp?:|saml:)?[A-Za-z][^>]*>[\s\S]{0,200}/gi;
const LONG_BASE64_PATTERN = /[A-Za-z0-9+/]{80,}={0,2}/g;

const MAX_MESSAGE_LENGTH = 500;

export const SKIP_LOG_URL_PATTERNS: ReadonlyArray<string> = [
  'metrics/engagement',
  'log-message',
  '/v2/saml2',
  '/saml2',
  'cognito-idp',
];

export const SENSITIVE_CONTEXT_KEYS: ReadonlySet<string> = new Set([
  'request',
  'saml',
  'samlresponse',
  'password',
  'token',
  'idtoken',
  'accesstoken',
  'refreshtoken',
  'authorization',
  'assertion',
  'username',
  'email',
  'code',
  'secret',
]);

const AUTH_ROUTE_PREFIXES: ReadonlyArray<string> = [
  '/login',
  '/register',
  '/activate',
  '/loginHelp',
  '/redirect/saml2',
];

export type DatadogLogEvent = {
  message?: string;
  status?: string;
  view?: { url?: string; referrer?: string };
  http?: { url?: string; status_code?: number };
  error?: { message?: string; stack?: string };
  [key: string]: unknown;
};

export type EnvironmentUrlSource = {
  customerGateway?: string;
  cardSsoLogUrl?: string;
  apiBaseUrl?: string;
  authUrl?: string;
  samlUrl?: string;
  cswUrl?: string;
  cardUrl?: string;
  referAFriendUrl?: string;
  cardSsoUrl?: string;
};

const extractHostname = (url: string): string | undefined => {
  try {
    return new URL(url).hostname;
  } catch {
    return undefined;
  }
};

export const buildAllowedHosts = (env: EnvironmentUrlSource): ReadonlySet<string> => {
  const hosts = new Set<string>();
  const urls = [
    env.customerGateway,
    env.cardSsoLogUrl,
    env.apiBaseUrl,
    env.authUrl,
    env.samlUrl,
    env.cswUrl,
    env.cardUrl,
    env.referAFriendUrl,
    env.cardSsoUrl,
    'cognito-idp.us-east-1.amazonaws.com',
  ];
  for (const url of urls) {
    const hostname = url ? extractHostname(url) : undefined;
    if (hostname) {
      hosts.add(hostname);
    }
  }
  return hosts;
};

const isAuthRoute = (pathname: string): boolean =>
  AUTH_ROUTE_PREFIXES.some((prefix) => pathname.startsWith(prefix));

const redactAllQueryParams = (query: string): string => {
  const params = new URLSearchParams(query);
  params.forEach((_value, key) => {
    params.set(key, REDACTED);
  });
  return params.toString();
};

export const maskUrlForLogging = (url: string, allowedHosts?: ReadonlySet<string>): string => {
  if (!url) return url;
  try {
    const parsed = new URL(url, 'https://placeholder.invalid');
    const hostname = parsed.hostname;
    const isAbsolute = url.startsWith('http://') || url.startsWith('https://');

    if (isAbsolute && allowedHosts && !allowedHosts.has(hostname)) {
      return `${parsed.protocol}//${REDACTED}/${REDACTED}`;
    }

    const maskedPath = parsed.pathname.replace(ID_SEGMENT_PATTERN, (_, parent) => {
      const singular = parent.endsWith('s') ? parent.slice(0, -1) : parent;
      return `/${parent}/{${singular}Id}`;
    });

    if (!parsed.search) {
      return isAbsolute ? `${parsed.origin}${maskedPath}` : maskedPath;
    }

    const query = isAuthRoute(parsed.pathname)
      ? redactAllQueryParams(parsed.search.slice(1))
      : redactAllQueryParams(parsed.search.slice(1));
    const origin = isAbsolute ? parsed.origin : '';
    return `${origin}${maskedPath}?${query}`;
  } catch {
    return REDACTED;
  }
};

export const maskRoutePath = (pathname: string): string => {
  if (!pathname) return pathname;
  return pathname.replace(ID_SEGMENT_PATTERN, (_, parent) => {
    const singular = parent.endsWith('s') ? parent.slice(0, -1) : parent;
    return `/${parent}/{${singular}Id}`;
  });
};

export const sanitizeLogMessage = (message: string | undefined): string => {
  if (!message) return 'Unknown error';
  let sanitized = message
    .replace(JWT_PATTERN, REDACTED)
    .replace(EMAIL_PATTERN, REDACTED)
    .replace(BEARER_PATTERN, REDACTED)
    .replace(SAML_XML_PATTERN, REDACTED)
    .replace(LONG_BASE64_PATTERN, REDACTED);
  if (sanitized.length > MAX_MESSAGE_LENGTH) {
    sanitized = `${sanitized.slice(0, MAX_MESSAGE_LENGTH)}…`;
  }
  return sanitized;
};

const looksLikeSamlContent = (value: unknown): boolean => {
  if (typeof value !== 'string') return false;
  const lower = value.toLowerCase();
  return (
    lower.includes('<samlp:') ||
    lower.includes('<saml:') ||
    lower.includes('samlresponse') ||
    (value.length > 200 && /^[A-Za-z0-9+/=]+$/.test(value))
  );
};

const sanitizeContextValue = (key: string, value: unknown, allowedHosts: ReadonlySet<string>): unknown => {
  if (SENSITIVE_CONTEXT_KEYS.has(key.toLowerCase())) {
    return REDACTED;
  }
  if (typeof value === 'string') {
    if (looksLikeSamlContent(value)) {
      return REDACTED;
    }
    if (value.startsWith('http://') || value.startsWith('https://') || value.startsWith('/')) {
      return maskUrlForLogging(value, allowedHosts);
    }
    return sanitizeLogMessage(value);
  }
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return sanitizeLogContext(value as Record<string, unknown>, allowedHosts);
  }
  return value;
};

export const sanitizeLogContext = (
  context: Record<string, unknown>,
  allowedHosts: ReadonlySet<string>
): Record<string, unknown> => {
  const sanitized: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(context)) {
    sanitized[key] = sanitizeContextValue(key, value, allowedHosts);
  }
  return sanitized;
};

const urlContainsSkipPattern = (url: string | undefined): boolean => {
  if (!url) return false;
  return SKIP_LOG_URL_PATTERNS.some((pattern) => url.includes(pattern));
};

const contextContainsSaml = (log: DatadogLogEvent): boolean => {
  for (const [key, value] of Object.entries(log)) {
    if (SENSITIVE_CONTEXT_KEYS.has(key.toLowerCase()) && looksLikeSamlContent(value)) {
      return true;
    }
    if (key.toLowerCase() === 'request' || key.toLowerCase() === 'saml') {
      return true;
    }
  }
  return false;
};

export const shouldDropDatadogLog = (log: DatadogLogEvent): boolean => {
  if (urlContainsSkipPattern(log.http?.url)) return true;
  if (urlContainsSkipPattern(log.view?.url)) return true;
  if (urlContainsSkipPattern(log.view?.referrer)) return true;

  const urlTemplate = log.url_template;
  if (typeof urlTemplate === 'string' && urlContainsSkipPattern(urlTemplate)) {
    return true;
  }

  const route = log.route;
  if (typeof route === 'string' && route.includes('/redirect/saml2')) {
    return true;
  }

  if (contextContainsSaml(log)) {
    return true;
  }

  return false;
};

export const sanitizeDatadogLogEvent = (
  log: DatadogLogEvent,
  allowedHosts: ReadonlySet<string>
): boolean => {
  if (shouldDropDatadogLog(log)) {
    return false;
  }

  if (log.message) {
    log.message = sanitizeLogMessage(log.message);
    if (log.message.includes('http')) {
      log.message = maskUrlForLogging(log.message, allowedHosts);
    }
  }

  if (log.view?.url) {
    log.view.url = maskUrlForLogging(log.view.url, allowedHosts);
  }
  if (log.view?.referrer) {
    log.view.referrer = maskUrlForLogging(log.view.referrer, allowedHosts);
  }
  if (log.http?.url) {
    log.http.url = maskUrlForLogging(log.http.url, allowedHosts);
  }
  if (log.error?.message) {
    log.error.message = sanitizeLogMessage(log.error.message);
  }
  if (log.error?.stack) {
    log.error.stack = sanitizeLogMessage(log.error.stack);
  }

  for (const [key, value] of Object.entries(log)) {
    if (['message', 'view', 'http', 'error', 'status', 'date', 'origin'].includes(key)) {
      continue;
    }
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      log[key] = sanitizeLogContext(value as Record<string, unknown>, allowedHosts);
    } else if (typeof value === 'string') {
      log[key] = sanitizeContextValue(key, value, allowedHosts);
    }
  }

  return true;
};

export const createDatadogLogsBeforeSend = (env: EnvironmentUrlSource) => {
  const allowedHosts = buildAllowedHosts(env);
  return (log: DatadogLogEvent): boolean => sanitizeDatadogLogEvent(log, allowedHosts);
};
